import { useState } from 'react'

import Button from '@components/designSystem/Button'
import { Calendar } from '@components/designSystem/date-picker/calendar'
import useAvailableScanDates from '@hooks/useAvailableScanDates.ts'
import { useCurrentStoreId } from '@hooks/useCurrentStore.ts'
import { usePageHeader } from '@hooks/useHeader.ts'
import { useMediaQuery } from '@hooks/useMediaQuery.ts'
import { useScan } from '@hooks/useScan'
import { addDayToLink, LATEST_DAY, useSelectedDay } from '@hooks/useSelectedDay'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { useStoreDays } from '@state/scans.ts'
import { useViewSettings } from '@state/viewSettings'
import { routesFactory } from '@utils/routes'
import { calculateStoreDays } from '@utils/storeDays.ts'

import type { DateValue } from 'react-aria-components'

function ChangeScan() {
  const { t } = useTypedTranslation()
  usePageHeader({ title: t('account.scan.title') })

  const currentStoreId = useCurrentStoreId()
  const isExtraSmallScreen = useMediaQuery('xs')

  const {
    currentScanDate,
    minScanDate,
    maxScanDate,
    isDateAvailable,
    getScanByDate,
  } = useAvailableScanDates()

  const [selectedScanDate, setSelectedScanDate] = useState<DateValue>(currentScanDate)
  const selectedScan = getScanByDate(selectedScanDate)

  return (
    <div className='relative flex h-full flex-col items-center justify-center gap-4'>
      <div className='flex size-full flex-col items-center justify-between'>
        <Calendar
          className='rounded-lg border border-secondary px-1 py-3 shadow-xs-skeumorphic'
          size={isExtraSmallScreen ? 'lg' : 'md'}
          aria-label={t('quickSelects.scan.name')}
          value={selectedScanDate}
          onChange={setSelectedScanDate}
          isDateUnavailable={(date) => !isDateAvailable(date)}
          minValue={minScanDate}
          maxValue={maxScanDate}
        />

      </div>
      <Button
        className='absolute bottom-12 w-full'
        href={routesFactory.account(currentStoreId, selectedScan?.id)}
        isDisabled={!selectedScan}
      >
        {t('common.save')}
      </Button>
    </div>
  )
}

function ChangeDay() {
  const { t } = useTypedTranslation()
  usePageHeader({ title: t('account.scan.title') })

  const currentStoreId = useCurrentStoreId()
  const isExtraSmallScreen = useMediaQuery('xs')
  const { day } = useSelectedDay()
  const { data: days = [] } = useStoreDays()
  const { currentDay, minDay, maxDay, isDayAvailable } = calculateStoreDays(days, day ?? LATEST_DAY)

  const [picked, setPicked] = useState<DateValue>()
  const value = picked ?? currentDay

  return (
    <div className='relative flex h-full flex-col items-center justify-center gap-4'>
      <div className='flex size-full flex-col items-center justify-between'>
        <Calendar
          className='rounded-lg border border-secondary px-1 py-3 shadow-xs-skeumorphic'
          size={isExtraSmallScreen ? 'lg' : 'md'}
          aria-label={t('quickSelects.day.name')}
          value={value ?? null}
          onChange={setPicked}
          isDateUnavailable={date => !isDayAvailable(date)}
          minValue={minDay}
          maxValue={maxDay}
        />
      </div>
      <Button
        className='absolute bottom-12 w-full'
        href={addDayToLink(routesFactory.account(currentStoreId, 'latest'), value?.toString() ?? null)}
        isDisabled={!value}
      >
        {t('common.save')}
      </Button>
    </div>
  )
}

function ChangeScanRoute() {
  const { isLatest } = useScan()
  const { data: settings } = useViewSettings()
  return settings?.dayViewIsDefault && isLatest ? <ChangeDay /> : <ChangeScan />
}

export default ChangeScanRoute